import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Navigation, ArrowRight, Loader2, Plane, MapPin, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RouteWeather = ({ getFlightCategoryStyle, favorites }) => {
  const [departure, setDeparture] = useState("");
  const [destination, setDestination] = useState("");
  const [routeData, setRouteData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [explanation, setExplanation] = useState(null);
  const [explaining, setExplaining] = useState(false);

  const searchRoute = async () => {
    const dep = departure.toUpperCase().trim();
    const dest = destination.toUpperCase().trim();

    if (dep.length !== 4 || dest.length !== 4) {
      toast.error("Please enter valid 4-character ICAO codes");
      return;
    }

    setLoading(true);
    setExplanation(null);

    try {
      const response = await axios.post(`${API}/weather/route`, {
        departure: dep,
        destination: dest
      });
      setRouteData(response.data);
    } catch (error) {
      toast.error("Failed to fetch route weather");
    } finally {
      setLoading(false);
    }
  };

  const generateRouteExplanation = async () => {
    if (!routeData) return;

    setExplaining(true);
    try {
      const combinedMetar = [
        routeData.departure.metar,
        routeData.destination.metar
      ].filter(Boolean).join('\n');

      const combinedTaf = [
        routeData.departure.taf,
        routeData.destination.taf
      ].filter(Boolean).join('\n');

      const response = await axios.post(`${API}/weather/explain`, {
        icao: `${routeData.departure.icao} → ${routeData.destination.icao}`,
        metar: combinedMetar,
        taf: combinedTaf
      });
      setExplanation(response.data.explanation);
      toast.success("Route briefing generated!");
    } catch (error) {
      toast.error("Failed to generate route explanation");
    } finally {
      setExplaining(false);
    }
  };

  const renderAirportWeather = (data, label) => (
    <div className="weather-card p-6" data-testid={`${label.toLowerCase()}-weather`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${label === 'Departure' ? 'bg-emerald-100 border-emerald-200' : 'bg-blue-100 border-blue-200'} border`}>
            {label === 'Departure' ? (
              <Plane className={`w-5 h-5 ${label === 'Departure' ? 'text-emerald-600' : 'text-blue-600'}`} />
            ) : (
              <MapPin className="w-5 h-5 text-blue-600" />
            )}
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-800">{label}</h3>
            <p className="text-sm text-slate-500 font-mono">{data.icao}</p>
          </div>
        </div>
        {data.flight_category && data.flight_category !== "UNKNOWN" && (
          <Badge className={`${getFlightCategoryStyle(data.flight_category)} border font-bold px-3 py-1`}>
            {data.flight_category}
          </Badge>
        )}
      </div>

      {data.metar ? (
        <div className="space-y-3">
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">METAR</p>
            <div className="metar-raw text-sm">
              {data.metar}
            </div>
          </div>
          {data.taf && (
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">TAF</p>
              <div className="metar-raw text-sm">
                {data.taf}
              </div>
            </div>
          )}
        </div>
      ) : (
        <p className="text-slate-400 text-center py-4">No weather data available</p>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Route Input */}
      <div className="weather-card p-6">
        <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
          <Navigation className="w-5 h-5 text-blue-600" />
          Flight Route Weather
        </h3>
        
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <div className="flex-1 w-full">
            <label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">
              Departure
            </label>
            <Input
              type="text"
              placeholder="ICAO (e.g., KJFK)"
              value={departure}
              onChange={(e) => setDeparture(e.target.value.toUpperCase())}
              className="search-input uppercase"
              maxLength={4}
              data-testid="departure-input"
            />
          </div>

          <ArrowRight className="w-6 h-6 text-slate-400 hidden sm:block mt-6" />

          <div className="flex-1 w-full">
            <label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">
              Destination
            </label>
            <Input
              type="text"
              placeholder="ICAO (e.g., KLAX)"
              value={destination}
              onChange={(e) => setDestination(e.target.value.toUpperCase())}
              className="search-input uppercase"
              maxLength={4}
              data-testid="destination-input"
            />
          </div>

          <Button
            onClick={searchRoute}
            disabled={loading || departure.length !== 4 || destination.length !== 4}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 h-12 mt-6 w-full sm:w-auto shadow-md"
            data-testid="get-route-weather-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              "Get Route Weather"
            )}
          </Button>
        </div>

        {/* Quick select from favorites */}
        {favorites.length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-200">
            <p className="text-xs text-slate-500 mb-2">Quick select from favorites:</p>
            <div className="flex flex-wrap gap-2">
              {favorites.slice(0, 6).map((fav) => (
                <Button
                  key={fav.id}
                  variant="outline"
                  size="sm"
                  className="border-slate-300 text-slate-600 hover:bg-slate-100"
                  onClick={() => {
                    if (!departure) setDeparture(fav.icao);
                    else if (!destination) setDestination(fav.icao);
                  }}
                >
                  {fav.icao}
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Route Results */}
      {routeData && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {renderAirportWeather(routeData.departure, "Departure")}
            {renderAirportWeather(routeData.destination, "Destination")}
          </div>

          {/* AI Route Briefing */}
          <div className="weather-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-100 border border-purple-200">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-800">
                    AI Route Briefing
                  </h3>
                  <p className="text-xs text-slate-500">
                    {routeData.departure.icao} → {routeData.destination.icao}
                  </p>
                </div>
              </div>
              <Button
                onClick={generateRouteExplanation}
                disabled={explaining}
                variant="outline"
                className="border-purple-300 text-purple-700 hover:bg-purple-50"
                data-testid="generate-route-explanation-btn"
              >
                {explaining ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    {explanation ? "Regenerate" : "Generate Briefing"}
                  </>
                )}
              </Button>
            </div>

            {explanation ? (
              <div className="explanation-container" data-testid="route-ai-explanation">
                <ScrollArea className="h-auto max-h-96">
                  <div className="prose prose-sm max-w-none">
                    {explanation.split('\n').map((paragraph, idx) => (
                      <p key={idx} className="text-slate-700 leading-relaxed mb-3 last:mb-0">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <p>Click "Generate Briefing" for an AI-powered route weather analysis</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Empty State */}
      {!routeData && !loading && (
        <div className="text-center py-12">
          <div className="inline-flex p-4 rounded-full bg-slate-100 mb-6">
            <Navigation className="w-12 h-12 text-slate-400" />
          </div>
          <h2 className="text-xl font-bold text-slate-700 mb-2">
            Plan Your Route
          </h2>
          <p className="text-slate-500 max-w-md mx-auto">
            Enter departure and destination ICAO codes to get weather briefings for your entire flight route.
          </p>
        </div>
      )}
    </div>
  );
};

export default RouteWeather;
